
import { supabase } from "@/integrations/supabase/client";
import { mapToDerivativeDetail } from '../mappers/derivativeMapper';

/**
 * Common function for fetching derivative data with consistent mapping
 */
export async function fetchDerivativeData(query: any) {
  try {
    const { data, error } = await query;
      
    if (error) {
      console.error('Error fetching derivative data:', error);
      return null;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    // Map to consistent format
    return Array.isArray(data) 
      ? data.map(item => mapToDerivativeDetail(item)) 
      : mapToDerivativeDetail(data);
  } catch (error) {
    console.error('Unexpected error in derivative fetch:', error);
    return null;
  }
}

/**
 * Base query builder for derivatives with common joins
 * Optimized to only select necessary fields that exist in the database
 */
export function createBaseDerivativeQuery() {
  return supabase
    .from('derivatives')
    .select(`
      id,
      title,
      description,
      thumbnail_url,
      video_url,
      expression_type,
      aspect_ratio,
      status,
      user_id,
      original_clip_id,
      playlist_id,
      submitted_date,
      approved_date,
      rejected_date,
      original_clips,
      review_comment
    `);
}

/**
 * Helper to cache creator profiles to reduce database calls
 */
const profileCache = new Map<string, { name: string, avatar: string }>();

/**
 * Fetch creator profiles efficiently with caching
 */
export async function fetchCreatorProfiles(creatorIds: string[]) {
  // Filter out IDs that are already cached
  const uncachedIds = creatorIds.filter(id => !profileCache.has(id));
  
  if (uncachedIds.length > 0) {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .in('id', uncachedIds);
      
    if (data) {
      data.forEach(profile => {
        profileCache.set(profile.id, {
          name: profile.full_name || `creator-${profile.id.substring(0, 8)}`,
          avatar: profile.avatar_url || ''
        });
      });
    }
  }
  
  // Create result object with all requested profiles
  const result: Record<string, { name: string, avatar: string }> = {};
  
  creatorIds.forEach(id => {
    if (profileCache.has(id)) {
      result[id] = profileCache.get(id)!;
    } else {
      // Fallback for any profile that wasn't found
      result[id] = {
        name: `creator-${id.substring(0, 8)}`,
        avatar: ''
      };
      // Add to cache to prevent future lookups
      profileCache.set(id, result[id]);
    }
  });
  
  return result;
}
